import { isEmptySlateDocument } from "../../../core/constants";
import RichTextRenderer from "../../../forms/components/RichText/RichTextRenderer";
import {
  CashRegistryDetails,
  Configuration,
  RegistryDetails,
  Section,
  SectionTypes,
} from "../../../resources/modelTypes";
import { themeTypographyResetStyles } from "../../../website/components/themeTypographyResetStyles";
import { DisplayPhoto } from "../DisplayPhoto";
import {
  paypalBadge,
  venmoBadge,
  cashRegistryDefaultSmall,
  registryDefaultSmall,
} from "../assets";
import { css } from "@emotion/react";
import {
  Button,
  Icon,
  Modal,
  styleUtils,
  theme,
} from "@minted/minted-components";
import React, { memo } from "react";

const modalStyles = {
  bodyContent: css`
    display: flex;
    flex-direction: row;
    min-height: ${styleUtils.rem(200)};
    gap: ${theme.spacing.x6};
    ${theme.media.lessThan(theme.breakpoints.small)(`
      flex-direction: column;
    `)};
  `,
  contentColumn: css`
    display: flow;
    flex-direction: column;
    flex: 1;
    gap: ${theme.spacing.x4};
    justify-content: flex-start;
    min-width: 0;
  `,
  description: css`
    ${theme.typeStyles.bodyMediumStyles};
    color: ${theme.colors.textPrimary};
    margin-bottom: ${theme.spacing.x6};
    overflow-wrap: break-word;
    word-break: break-word;
  `,
  imageColumn: css`
    aspect-ratio: 1;
    flex-shrink: 0;
    overflow: hidden;
    height: ${styleUtils.rem(304)};
    width: ${styleUtils.rem(304)};
    margin: 0 auto;
    & img {
      border-radius: ${styleUtils.rem(8)};
      height: 100%;
      object-fit: cover;
      width: 100%;
    }

    ${theme.media.lessThan(theme.breakpoints.small)(`
      height: auto;
      width: 100%;
      height: ${styleUtils.rem(295)};
      width: ${styleUtils.rem(295)};
    `)};
  `,
  imageInner: css`
    height: 100%;
    width: 100%;
  `,
  secondaryLink: css`
    ${theme.typeStyles.bodySmallStyles};
    color: ${theme.colors.textSecondary};
  `,
  title: css`
    ${theme.typeStyles.titleExtraLargeStyles};
    color: ${theme.colors.textPrimary};
    margin-bottom: ${theme.spacing.x2};
    margin-top: 0;
  `,
  secondaryText: css`
    ${theme.typeStyles.bodySmallStyles};
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x2};
    text-align: center;
  `,
  badge: css`
    height: ${styleUtils.rem(24)};
    object-fit: contain;
  `,
};

export type RegistryDetailModalProps = {
  configuration: Configuration;
  details: RegistryDetails | CashRegistryDetails;
  onClose: () => void;
  open: boolean;
  section: Section;
  onContributeClick: () => void;
};

const BADGES = {
  venmo: venmoBadge,
  paypal: paypalBadge,
};

const PROVIDER_LABELS: Record<string, string> = {
  paypal: "PayPal",
  venmo: "Venmo",
};

function getContributeUrl(
  provider: string | undefined,
  username: string | undefined,
): string | null {
  if (!provider || !username) {
    return null;
  }
  const normalizedUsername = username.replace(/^@/, "");
  if (provider === "paypal") {
    return `https://paypal.me/${normalizedUsername}`;
  }
  if (provider === "venmo") {
    return `https://venmo.com/u/${normalizedUsername}`;
  }
  return null;
}

const RegistryDetailModal = ({
  configuration,
  details,
  onClose,
  open,
  section,
  onContributeClick = () => {},
}: RegistryDetailModalProps) => {
  const hasContent = !isEmptySlateDocument(details.content);
  const isCashRegistry = section.sectionType === SectionTypes.CashRegistry;
  const cashDetails = isCashRegistry && "provider" in details ? details : null;

  return (
    <Modal
      closeOnEsc={true}
      closeOnOverlayClick={true}
      displayCloseButton={true}
      onCloseClick={onClose}
      open={open}
      size={Modal.sizes.medium}
    >
      <Modal.Body>
        <div css={[modalStyles.bodyContent, themeTypographyResetStyles]}>
          <div css={modalStyles.imageColumn}>
            <div css={modalStyles.imageInner}>
              <DisplayPhoto
                configuration={configuration}
                isRegistryPhoto
                section={section}
                defaultImage={
                  isCashRegistry
                    ? cashRegistryDefaultSmall
                    : registryDefaultSmall
                }
              />
            </div>
          </div>
          <div css={modalStyles.contentColumn}>
            {cashDetails?.provider && cashDetails.provider in BADGES && (
              <img
                src={BADGES[cashDetails.provider as keyof typeof BADGES]}
                alt={cashDetails.provider}
                css={modalStyles.badge}
              />
            )}

            {details.title && (
              <div aria-level={2} css={modalStyles.title} role="heading">
                {details.title}
              </div>
            )}
            <div css={modalStyles.description}>
              {hasContent ? (
                <RichTextRenderer value={details.content} />
              ) : "textDescription" in details ? (
                (details as CashRegistryDetails).textDescription
              ) : null}
            </div>
            {details.websiteUrl && (
              <>
                <Button
                  expand
                  iconAlignment="right"
                  iconType={Icon.types["open-in-new"]}
                  onClick={() => window.open(details.websiteUrl!, "_blank")}
                  size={Button.sizes.medium}
                  text={details.buttonText || "VISIT REGISTRY"}
                  type={Button.types.primary}
                />
              </>
            )}

            {isCashRegistry && cashDetails && (
              <Button
                expand
                iconAlignment="right"
                iconType={Icon.types["open-in-new"]}
                onClick={() => {
                  const contributeUrl = getContributeUrl(
                    cashDetails.provider,
                    cashDetails.username,
                  );
                  if (contributeUrl) {
                    window.open(contributeUrl, "_blank");
                  }
                  onContributeClick();
                }}
                size={Button.sizes.medium}
                text={`CONTRIBUTE ON ${(cashDetails.provider ?? "EXTERNAL").toUpperCase()}`}
                type={Button.types.primary}
              />
            )}
            {section.sectionType === SectionTypes.CashRegistry ? (
              <div css={modalStyles.secondaryText}>
                Minted doesn&apos;t process payments. You&apos;ll complete your
                gift securely on{" "}
                {cashDetails?.provider &&
                cashDetails.provider in PROVIDER_LABELS
                  ? PROVIDER_LABELS[cashDetails.provider]
                  : "the selected service"}
                .
              </div>
            ) : (
              details.title && (
                <div css={modalStyles.secondaryText}>
                  View this registry on {details.title}
                </div>
              )
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const MemoizedRegistryDetailModal = memo(RegistryDetailModal);

export { MemoizedRegistryDetailModal as RegistryDetailModal };
